import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { ApiError } from '@/api/client';
import { useSession } from '@/auth/SessionContext';
import { type ChatAttachmentView } from '@/components/ChatMessageAttachments';
import { ChatMessageBubble, ChatAlbumRow, ChatTypingBubble } from '@/components/ChatMessageBubble';
import { groupConsecutiveImageMessages } from '@/lib/groupConsecutiveImageMessages';
import { ConversationChannelIcon } from '@/components/ConversationChannelIcon';
import { IconCalendar } from '@/components/TabIcons';
import { channelLabel } from '@/calendar/channelMeta';
import { ScreenBackButton } from '@/components/ScreenBackButton';
import {
  displayVisitorLabel,
  formatControlMode,
  formatConversationDateTime,
  formatConversationStatus,
  isAutoGeneratedVisitorName,
  sanitizeVisitorName,
} from '@/lib/conversationDisplay';
import { isFixedHandoffAgentMessage } from '@/lib/fixedHandoffMessage';
import {
  CHAT_ACCEPT,
  CHAT_MAX_FILES,
  chunkChatFiles,
  filesToAttachmentPayload,
  validateChatFile,
} from '@/lib/chatAttachments';
import { refreshPendingChatsBadge } from '@/hooks/usePendingChatsBadgeCount';
import { isAbortError, pollRequestInit } from '@/lib/pollFetch';
import { VoiceNoteRecorder, blobToBase64 } from '@/components/VoiceNoteRecorder';
import {
  formatWhatsappWindowRemaining,
  remainingWindowMs,
} from '@/lib/whatsappCustomerWindow';
import {
  isRetryableOutboundStatus,
  isWhatsappChannel,
  outboundRetryReady,
  shouldAutoRetryOutbound,
} from '@/lib/outboundDeliveryRetry';
import { PwaLeadQualityBlock } from '@/components/PwaLeadQualityBlock';
import {
  ChatReservationSheet,
  type ReservationPrefill,
} from '@/components/ChatReservationSheet';
import { ChatAttachSheet } from '@/components/ChatAttachSheet';
import {
  fetchResourceMediaBlob,
  loadResourceChatImage,
  EMPTY_RESOURCE_PHOTO_GROUPS,
  resourcePhotoGroups,
  type ResourceImageRef,
  type ResourcePhotoGroup,
} from '@/lib/resourceChatMedia';

type Msg = {
  id: string;
  content: string;
  sender: string;
  timestamp: string;
  agentSource?: string | null;
  isWhisper?: boolean;
  attachments?: ChatAttachmentView[];
  audioTranscription?: string | null;
  transcriptionStatus?: string | null;
  deliveryStatus?: string | null;
  operatorRating?: string | null;
  operatorFeedbackComment?: string | null;
  operatorFeedbackDesired?: string | null;
};

type ConvDetail = {
  id: string;
  channel: string;
  visitorName: string | null;
  controlMode: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  externalThreadId?: string | null;
  channelIntegrationId?: string | null;
  messages: Msg[];
  virtualAgent: { id: string; name: string } | null;
  workflow?: { id: string; name: string } | null;
  visitorTyping?: boolean;
  whatsappCustomerWindow?: {
    channel: string;
    lastVisitorAt: string | null;
    remainingMs: number;
    showCountdown: boolean;
    lockComposer: boolean;
    templates: Array<{ id: string; name: string; languageCode: string }>;
  } | null;
  leadQuality?: string | null;
  leadReason?: string | null;
  leadFitDetails?: string[];
  leadNote?: string | null;
  leadRatedSource?: string | null;
  leadMode?: 'ads' | 'internal' | null;
  leadStates?: Array<{ id: string; label: string; color?: string }> | null;
  hasLeadQualificationPlugin?: boolean;
};

function conversationPath(id: string): string {
  const t = Date.now();
  return `/api/console/conversations/${id}?_=${t}`;
}

const POLL_MS_VISIBLE = 1200;
const POLL_MS_HIDDEN = 6000;
const TOUCH_THROTTLE_MS = 700;
/** Si el GET no responde, el poll se corta y el chain sigue (evita chat “congelado”). */
const POLL_FETCH_TIMEOUT_MS = 28_000;

export function ConversationThreadPage() {
  const { id: routeId } = useParams<{ id: string }>();
  const { get, post, patch, can, jwt } = useSession();
  const getRef = useRef(get);
  getRef.current = get;
  const idRef = useRef(routeId);
  idRef.current = routeId;

  const [conv, setConv] = useState<ConvDetail | null>(null);
  const [text, setText] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [attachOpen, setAttachOpen] = useState(false);
  const [photoGroups, setPhotoGroups] = useState<ResourcePhotoGroup[] | null>(null);
  const [selectedMediaKeys, setSelectedMediaKeys] = useState<Set<string>>(() => new Set());
  const [busyMediaKey, setBusyMediaKey] = useState<string | null>(null);
  const [attachErr, setAttachErr] = useState<string | null>(null);
  const mediaFilesRef = useRef(new Map<string, File>());
  const pendingFilesRef = useRef(pendingFiles);
  const attachingCountRef = useRef(0);
  pendingFilesRef.current = pendingFiles;
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [hasContactBlacklist, setHasContactBlacklist] = useState(false);
  const [contactBlocked, setContactBlocked] = useState(false);
  const [blacklistBusy, setBlacklistBusy] = useState(false);
  const canManageBlacklist = can('pwa.conversations.write');
  const [infoOpen, setInfoOpen] = useState(false);
  const [hasAgentCalendar, setHasAgentCalendar] = useState(false);
  const [reserveOpen, setReserveOpen] = useState(false);
  const [reservePrefill, setReservePrefill] = useState<ReservationPrefill | null>(null);
  const [reserveNotice, setReserveNotice] = useState<string | null>(null);
  const location = useLocation();
  const openedReservationNav = useRef(false);
  const canReserve = can('pwa.calendar.write');
  const [improveForId, setImproveForId] = useState<string | null>(null);
  const [improveComment, setImproveComment] = useState('');
  const [improveDesired, setImproveDesired] = useState('');
  const [improveBusy, setImproveBusy] = useState(false);
  const [lockedHint, setLockedHint] = useState<string | null>(null);
  const [deleteIds, setDeleteIds] = useState<string[] | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingIdleRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  /** El último GET de detalle (poll o reload) gana; evita snapshots viejos por polls solapados. */
  const detailFetchSeqRef = useRef(0);
  const autoSkipIdsRef = useRef(new Set<string>());
  const autoAttemptCountRef = useRef(new Map<string, number>());
  const retryInFlightRef = useRef(new Set<string>());
  const lastRetryAtRef = useRef(new Map<string, number>());
  const [retryingIds, setRetryingIds] = useState<Set<string>>(() => new Set());
  const [deliveryClock, setDeliveryClock] = useState(() => Date.now());
  const [windowClock, setWindowClock] = useState(() => Date.now());
  const [templateId, setTemplateId] = useState('');
  const [sendingTemplate, setSendingTemplate] = useState(false);

  const signalOperatorTyping = useCallback(
    (active: boolean) => {
      if (!routeId) return;
      void (async () => {
        try {
          await post(`/api/console/conversations/${routeId}/operator-typing`, { active });
        } catch {
          /* */
        }
      })();
    },
    [routeId, post]
  );

  const reload = useCallback(async () => {
    if (!routeId) return;
    const seq = ++detailFetchSeqRef.current;
    try {
      const data = await getRef.current<ConvDetail>(
        conversationPath(routeId),
        pollRequestInit(POLL_FETCH_TIMEOUT_MS)
      );
      if (seq !== detailFetchSeqRef.current) return;
      setConv(data);
    } catch (e) {
      if (isAbortError(e)) {
        throw new ApiError('Tiempo de espera al cargar la conversación', 0);
      }
      throw e;
    }
  }, [routeId]);

  useEffect(() => {
    if (!routeId) return;
    let cancelled = false;
    let chainTimer: ReturnType<typeof setTimeout> | null = null;
    let lastTouchPoll = 0;

    const pollDelay = () =>
      document.visibilityState === 'visible' ? POLL_MS_VISIBLE : POLL_MS_HIDDEN;

    const fetchOne = async (isInitial: boolean): Promise<void> => {
      const cid = idRef.current;
      if (cancelled || !cid) return;
      const seq = ++detailFetchSeqRef.current;
      try {
        const data = await getRef.current<ConvDetail>(
          conversationPath(cid),
          pollRequestInit(POLL_FETCH_TIMEOUT_MS)
        );
        if (cancelled || idRef.current !== cid || seq !== detailFetchSeqRef.current) return;
        setConv(data);
        setErr(null);
      } catch (e) {
        if (cancelled || idRef.current !== cid || seq !== detailFetchSeqRef.current) return;
        if (isAbortError(e)) {
          if (isInitial) setErr('Sin respuesta del servidor. Reintentá.');
          return;
        }
        if (isInitial) setErr(e instanceof ApiError ? e.message : 'Error');
      }
    };

    const scheduleNext = () => {
      if (cancelled) return;
      if (chainTimer != null) {
        window.clearTimeout(chainTimer);
        chainTimer = null;
      }
      chainTimer = window.setTimeout(() => {
        chainTimer = null;
        void (async () => {
          try {
            await fetchOne(false);
          } finally {
            if (!cancelled) scheduleNext();
          }
        })();
      }, pollDelay());
    };

    const bumpFromInteraction = () => {
      const now = Date.now();
      if (now - lastTouchPoll < TOUCH_THROTTLE_MS) return;
      lastTouchPoll = now;
      void fetchOne(false);
    };

    void (async () => {
      try {
        await fetchOne(true);
      } finally {
        if (!cancelled) scheduleNext();
      }
    })();

    const onOnline = () => {
      if (cancelled) return;
      void fetchOne(false);
      scheduleNext();
    };

    const onVis = () => {
      if (cancelled) return;
      void fetchOne(false);
      scheduleNext();
    };

    const onResume = () => {
      if (cancelled) return;
      void fetchOne(false);
    };

    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onResume);
    window.addEventListener('pageshow', onResume);
    window.addEventListener('touchstart', bumpFromInteraction, { passive: true });
    window.addEventListener('pointerdown', bumpFromInteraction, { passive: true });
    window.addEventListener('online', onOnline);

    return () => {
      cancelled = true;
      detailFetchSeqRef.current += 1;
      if (chainTimer != null) window.clearTimeout(chainTimer);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onResume);
      window.removeEventListener('pageshow', onResume);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('touchstart', bumpFromInteraction);
      window.removeEventListener('pointerdown', bumpFromInteraction);
    };
  }, [routeId]);

  useEffect(() => {
    autoSkipIdsRef.current = new Set();
    autoAttemptCountRef.current = new Map();
    retryInFlightRef.current = new Set();
    lastRetryAtRef.current = new Map();
    setRetryingIds(new Set());
  }, [routeId]);

  useEffect(() => {
    const tick = window.setInterval(() => setDeliveryClock(Date.now()), 4000);
    return () => window.clearInterval(tick);
  }, []);

  const windowInfo = conv?.whatsappCustomerWindow;
  const showWindowUi = Boolean(windowInfo?.showCountdown || windowInfo?.lockComposer);
  const templateKey = (windowInfo?.templates ?? []).map((t) => t.id).join(',');

  useEffect(() => {
    if (!showWindowUi) return;
    const tick = window.setInterval(() => setWindowClock(Date.now()), 1000);
    return () => window.clearInterval(tick);
  }, [showWindowUi]);

  useEffect(() => {
    const templates = conv?.whatsappCustomerWindow?.templates ?? [];
    if (!templates.length) {
      setTemplateId('');
      return;
    }
    setTemplateId((prev) => (templates.some((t) => t.id === prev) ? prev : templates[0]!.id));
  }, [conv?.id, templateKey]);

  const retryDelivery = useCallback(
    async (messageId: string, opts?: { auto?: boolean }) => {
      if (!routeId || retryInFlightRef.current.has(messageId)) return false;
      retryInFlightRef.current.add(messageId);
      setRetryingIds((prev) => new Set(prev).add(messageId));
      if (!opts?.auto) setErr(null);
      try {
        const updated = await post<ConvDetail>(
          `/api/console/conversations/${routeId}/messages/${encodeURIComponent(messageId)}/retry-delivery`,
          {}
        );
        lastRetryAtRef.current.set(messageId, Date.now());
        if (opts?.auto) {
          autoAttemptCountRef.current.set(messageId, (autoAttemptCountRef.current.get(messageId) ?? 0) + 1);
        }
        detailFetchSeqRef.current += 1;
        setConv(updated);
        return true;
      } catch (e) {
        const code = e instanceof ApiError ? e.code : undefined;
        if (opts?.auto && e instanceof ApiError && e.status === 409 && code === 'TOO_EARLY') {
          return false;
        }
        lastRetryAtRef.current.set(messageId, Date.now());
        if (opts?.auto) {
          autoAttemptCountRef.current.set(messageId, (autoAttemptCountRef.current.get(messageId) ?? 0) + 1);
        }
        if (code === 'NOT_RETRYABLE' || code === 'CHANNEL' || code === 'NO_THREAD' || code === 'WINDOW_CLOSED') {
          autoSkipIdsRef.current.add(messageId);
        }
        if (!opts?.auto) {
          setErr(e instanceof ApiError ? e.message : 'No se pudo reenviar');
        }
        return false;
      } finally {
        retryInFlightRef.current.delete(messageId);
        setRetryingIds((prev) => {
          const next = new Set(prev);
          next.delete(messageId);
          return next;
        });
      }
    },
    [routeId, post]
  );

  useEffect(() => {
    if (!canManageBlacklist || !conv || conv.id !== routeId || !isWhatsappChannel(conv.channel)) return;
    if (conv.whatsappCustomerWindow?.lockComposer) return;
    if (retryInFlightRef.current.size > 0) return;
    const now = deliveryClock;
    const next = conv.messages.find(
      (m) =>
        !autoSkipIdsRef.current.has(m.id) &&
        shouldAutoRetryOutbound({
          channel: conv.channel,
          sender: m.sender,
          isWhisper: m.isWhisper,
          deliveryStatus: m.deliveryStatus,
          timestamp: m.timestamp,
          now,
          lastAttemptAt: lastRetryAtRef.current.get(m.id),
          autoAttemptCount: autoAttemptCountRef.current.get(m.id) ?? 0,
        })
    );
    if (!next) return;
    void retryDelivery(next.id, { auto: true });
  }, [conv, canManageBlacklist, retryDelivery, deliveryClock, routeId]);

  const mensajeId = new URLSearchParams(location.search).get('mensaje');

  useEffect(() => {
    const idRaf = requestAnimationFrame(() => {
      if (mensajeId) {
        const target = document.getElementById(`mensaje-${mensajeId}`);
        if (target) {
          target.scrollIntoView({ block: 'center' });
          return;
        }
      }
      const el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
    return () => cancelAnimationFrame(idRaf);
  }, [conv?.messages.length, conv?.visitorTyping, routeId, mensajeId]);

  useEffect(() => {
    return () => {
      if (!routeId) return;
      void post(`/api/console/conversations/${routeId}/operator-typing`, { active: false }).catch(() => {});
    };
  }, [routeId, post]);

  const onPickFiles = useCallback((list: FileList | null) => {
    if (!list?.length) return;
    const incoming = Array.from(list).slice(0, CHAT_MAX_FILES);
    for (const file of incoming) {
      const fileError = validateChatFile(file);
      if (fileError) {
        setErr(fileError);
        return;
      }
    }
    setErr(null);
    setPendingFiles([...pendingFiles, ...incoming]);
  }, [pendingFiles]);

  const sendHuman = useCallback(async (): Promise<boolean> => {
    if (!routeId) return false;
    const trimmed = text.trim();
    if (!trimmed && pendingFiles.length === 0) return false;
    for (const file of pendingFiles) {
      const fileError = validateChatFile(file);
      if (fileError) {
        setErr(fileError);
        setAttachErr(fileError);
        return false;
      }
    }
    setErr(null);
    setSending(true);
    try {
      const batches = chunkChatFiles(pendingFiles);
      if (batches.length === 0) {
        await post(`/api/console/conversations/${routeId}/agent-messages`, { content: trimmed });
      } else {
        for (let index = 0; index < batches.length; index += 1) {
          const attachments = await filesToAttachmentPayload(batches[index] ?? []);
          await post(`/api/console/conversations/${routeId}/agent-messages`, {
            content: index === 0 ? trimmed : '',
            ...(attachments.length ? { attachments } : {}),
          });
        }
      }
      signalOperatorTyping(false);
      if (typingIdleRef.current) {
        clearTimeout(typingIdleRef.current);
        typingIdleRef.current = null;
      }
      setText('');
      setPendingFiles([]);
      mediaFilesRef.current.clear();
      setSelectedMediaKeys(new Set());
      await reload();
      refreshPendingChatsBadge();
      return true;
    } catch (e) {
      const message = e instanceof ApiError ? e.message : 'No se pudo enviar';
      setErr(message);
      setAttachErr(message);
      return false;
    } finally {
      setSending(false);
    }
  }, [routeId, text, pendingFiles, post, reload, signalOperatorTyping]);

  const sendVoiceNote = useCallback(
    async (blob: Blob, mimeType: string) => {
      if (!routeId) return;
      setErr(null);
      setSending(true);
      try {
        const audioBase64 = await blobToBase64(blob);
        const updated = await post<ConvDetail>(`/api/console/conversations/${routeId}/agent-messages`, {
          content: '',
          audioBase64,
          audioMimeType: mimeType,
        });
        signalOperatorTyping(false);
        setConv(updated);
        refreshPendingChatsBadge();
        const lastAudio = [...(updated.messages ?? [])]
          .reverse()
          .find(
            (m) =>
              m.sender !== 'visitor' &&
              m.agentSource === 'human' &&
              m.attachments?.some((a) => a.kind === 'audio')
          );
        if (lastAudio?.deliveryStatus === 'failed') {
          setErr('No se pudo entregar el audio a WhatsApp');
        }
      } catch (e) {
        setErr(e instanceof ApiError ? e.message : 'No se pudo enviar el audio');
      } finally {
        setSending(false);
      }
    },
    [routeId, post, signalOperatorTyping]
  );

  const setMode = useCallback(
    async (mode: 'human' | 'auto') => {
      if (!routeId) return;
      setErr(null);
      try {
        await patch(`/api/console/conversations/${routeId}`, { controlMode: mode });
        await reload();
        refreshPendingChatsBadge();
      } catch (e) {
        setErr(e instanceof ApiError ? e.message : 'No se pudo actualizar');
      }
    },
    [routeId, patch, reload]
  );

  const sendTemplate = useCallback(
    async () => {
      if (!routeId || !templateId) return;
      setSendingTemplate(true);
      setErr(null);
      try {
        const updated = await post<ConvDetail>(`/api/console/conversations/${routeId}/send-template`, {
          templateId,
        });
        detailFetchSeqRef.current += 1;
        setConv(updated);
        refreshPendingChatsBadge();
      } catch (e) {
        setErr(e instanceof ApiError ? e.message : 'No se pudo enviar la plantilla');
      } finally {
        setSendingTemplate(false);
      }
    },
    [routeId, post, templateId]
  );

  const openImprove = useCallback(
    (m: {
      id: string;
      agentSource?: string | null;
      content?: string | null;
      operatorFeedbackComment?: string | null;
      operatorFeedbackDesired?: string | null;
    }) => {
      if (isFixedHandoffAgentMessage(m)) {
        setImproveForId(null);
        setLockedHint(
          'Este mensaje no se puede cambiar porque es fijo (handoff a humano). Editá el texto en Handoff del agente si querés otro mensaje.'
        );
        window.setTimeout(() => setLockedHint(null), 4200);
        return;
      }
      if (improveForId === m.id) {
        setImproveForId(null);
        return;
      }
      setImproveComment(m.operatorFeedbackComment ?? '');
      setImproveDesired(m.operatorFeedbackDesired ?? '');
      setImproveForId(m.id);
      setErr(null);
      setLockedHint(null);
    },
    [improveForId]
  );

  const submitImprove = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!routeId || !improveForId) return;
      const note = improveComment.trim();
      const desired = improveDesired.trim();
      if (!note && !desired) {
        setErr('Indicá qué estuvo mal o cómo debería haber respondido (al menos uno).');
        return;
      }
      const msgs = conv?.messages ?? [];
      const idx = msgs.findIndex((m) => m.id === improveForId);
      let userMessage: string | undefined;
      if (idx > 0) {
        for (let i = idx - 1; i >= 0; i--) {
          const prev = msgs[i];
          if (prev?.sender === 'visitor' && !prev.isWhisper) {
            const t = (prev.content ?? '').trim();
            if (t && t !== '[Archivos adjuntos]' && t !== '[Audio]') {
              userMessage = t;
              break;
            }
          }
        }
      }
      const addToAgentGuide = Boolean(conv?.virtualAgent) && Boolean(userMessage);
      setImproveBusy(true);
      setErr(null);
      try {
        await patch(`/api/console/conversations/${routeId}/messages/${improveForId}`, {
          operatorRating: 'bad',
          operatorFeedbackComment: note || undefined,
          operatorFeedbackDesired: desired || undefined,
          addToAgentGuide,
          userMessage,
        });
        setImproveForId(null);
        await reload();
      } catch (ex) {
        setErr(ex instanceof ApiError ? ex.message : 'No se pudo guardar la corrección');
      } finally {
        setImproveBusy(false);
      }
    },
    [
      routeId,
      improveForId,
      improveComment,
      improveDesired,
      conv?.virtualAgent,
      conv?.messages,
      patch,
      reload,
    ]
  );

  const loadContactBlacklistStatus = useCallback(async () => {
    if (!routeId || !hasContactBlacklist) {
      setContactBlocked(false);
      return;
    }
    try {
      const data = await get<{ blocked: boolean }>(
        `/api/console/conversations/${routeId}/contact-blacklist/status`
      );
      setContactBlocked(Boolean(data.blocked));
    } catch {
      setContactBlocked(false);
    }
  }, [routeId, hasContactBlacklist, get]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const d = await get<{ hasPlugin: boolean }>('/api/console/contact-blacklist/has-plugin');
        if (!cancelled) setHasContactBlacklist(Boolean(d.hasPlugin));
      } catch {
        if (!cancelled) setHasContactBlacklist(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [get]);

  useEffect(() => {
    void loadContactBlacklistStatus();
  }, [loadContactBlacklistStatus]);

  const agentId = conv?.virtualAgent?.id ?? null;

  useEffect(() => {
    setPhotoGroups(null);
    setAttachOpen(false);
    if (!agentId) {
      setPhotoGroups([]);
      return;
    }
    let cancelled = false;
    void get<unknown[]>(`/api/agents/${agentId}/calendar/resources`)
      .then((rows) => {
        if (!cancelled) setPhotoGroups(resourcePhotoGroups(rows));
      })
      .catch(() => {
        if (!cancelled) setPhotoGroups([]);
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, get]);

  const loadAttachPreview = useCallback(
    async (group: ResourcePhotoGroup, image: ResourceImageRef) => {
      if (!agentId) throw new Error('Sin agente');
      const blob = await fetchResourceMediaBlob(agentId, group.id, image.id, jwt);
      return URL.createObjectURL(blob);
    },
    [agentId, jwt]
  );

  const toggleResourcePhoto = useCallback(
    async (group: ResourcePhotoGroup, image: ResourceImageRef) => {
      const key = `${group.id}:${image.id}`;
      const existing = mediaFilesRef.current.get(key);
      if (existing) {
        mediaFilesRef.current.delete(key);
        setSelectedMediaKeys((current) => {
          const next = new Set(current);
          next.delete(key);
          return next;
        });
        setPendingFiles((prev) => {
          const next = prev.filter((file) => file !== existing);
          pendingFilesRef.current = next;
          return next;
        });
        setAttachErr(null);
        return;
      }
      if (!agentId || attachingCountRef.current > 0) return;
      attachingCountRef.current += 1;
      setBusyMediaKey(key);
      setAttachErr(null);
      try {
        const file = await loadResourceChatImage({ agentId, token: jwt, group, image });
        const validation = validateChatFile(file);
        if (validation) {
          setAttachErr(validation);
          return;
        }
        const next = [...pendingFilesRef.current, file];
        mediaFilesRef.current.set(key, file);
        pendingFilesRef.current = next;
        setPendingFiles(next);
        setSelectedMediaKeys((current) => new Set(current).add(key));
      } catch (e) {
        setAttachErr(e instanceof Error ? e.message : 'No se pudo adjuntar la foto');
      } finally {
        attachingCountRef.current = Math.max(0, attachingCountRef.current - 1);
        setBusyMediaKey(null);
      }
    },
    [agentId, jwt]
  );

  const selectAllResourcePhotos = useCallback(
    async (group: ResourcePhotoGroup) => {
      if (!agentId || attachingCountRef.current > 0) return;
      const keys = group.images.map((image) => `${group.id}:${image.id}`);
      const selectedFiles = keys
        .map((key) => mediaFilesRef.current.get(key))
        .filter((file): file is File => Boolean(file));
      if (selectedFiles.length === keys.length) {
        for (const key of keys) mediaFilesRef.current.delete(key);
        const drop = new Set(selectedFiles);
        const next = pendingFilesRef.current.filter((file) => !drop.has(file));
        pendingFilesRef.current = next;
        setPendingFiles(next);
        setSelectedMediaKeys((current) => {
          const remaining = new Set(current);
          for (const key of keys) remaining.delete(key);
          return remaining;
        });
        setAttachErr(null);
        return;
      }
      const missing = group.images.filter((image) => !mediaFilesRef.current.has(`${group.id}:${image.id}`));
      attachingCountRef.current += 1;
      setBusyMediaKey(`${group.id}:all`);
      setAttachErr(null);
      try {
        const loaded: { key: string; file: File }[] = [];
        let firstError: string | null = null;
        await Promise.all(
          missing.map(async (image) => {
            const key = `${group.id}:${image.id}`;
            try {
              const file = await loadResourceChatImage({ agentId, token: jwt, group, image });
              const validation = validateChatFile(file);
              if (validation) {
                firstError ??= validation;
                return;
              }
              loaded.push({ key, file });
            } catch (e) {
              firstError ??= e instanceof Error ? e.message : 'No se pudo adjuntar la foto';
            }
          })
        );
        if (loaded.length > 0) {
          const next = [...pendingFilesRef.current];
          for (const item of loaded) {
            if (mediaFilesRef.current.has(item.key)) continue;
            mediaFilesRef.current.set(item.key, item.file);
            next.push(item.file);
          }
          pendingFilesRef.current = next;
          setPendingFiles(next);
          setSelectedMediaKeys((current) => {
            const nextKeys = new Set(current);
            for (const item of loaded) nextKeys.add(item.key);
            return nextKeys;
          });
        }
        if (firstError) setAttachErr(firstError);
      } finally {
        attachingCountRef.current = Math.max(0, attachingCountRef.current - 1);
        setBusyMediaKey(null);
      }
    },
    [agentId, jwt]
  );

  function removePendingFile(index: number) {
    const file = pendingFiles[index];
    if (file) {
      for (const [key, mapped] of mediaFilesRef.current) {
        if (mapped !== file) continue;
        mediaFilesRef.current.delete(key);
        setSelectedMediaKeys((current) => {
          const next = new Set(current);
          next.delete(key);
          return next;
        });
      }
    }
    setPendingFiles((prev) => prev.filter((_, j) => j !== index));
  }

  function openAttach() {
    if (photoGroups && photoGroups.length === 0) {
      fileInputRef.current?.click();
      return;
    }
    setAttachErr(null);
    setAttachOpen(true);
  }

  useEffect(() => {
    if (!agentId || !canReserve) {
      setHasAgentCalendar(false);
      return;
    }
    let cancelled = false;
    void get<{ hasCalendar: boolean }>(`/api/console/calendario/has-calendar?agentId=${agentId}`)
      .then((d) => {
        if (!cancelled) setHasAgentCalendar(Boolean(d.hasCalendar));
      })
      .catch(() => {
        if (!cancelled) setHasAgentCalendar(false);
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, canReserve, get]);

  useEffect(() => {
    if (openedReservationNav.current || !canReserve) return;
    const state = location.state as { openReservation?: ReservationPrefill } | null;
    const wants =
      Boolean(state?.openReservation) ||
      new URLSearchParams(location.search).get('reservar') === '1';
    if (!wants) return;
    openedReservationNav.current = true;
    setReservePrefill(state?.openReservation ?? null);
    setReserveOpen(true);
  }, [canReserve, location.key, location.search, location.state]);

  const toggleContactBlacklist = useCallback(async () => {
    if (!routeId || !canManageBlacklist) return;
    setBlacklistBusy(true);
    setErr(null);
    try {
      if (contactBlocked) {
        await post(`/api/console/conversations/${routeId}/contact-blacklist/remove`, {});
        setContactBlocked(false);
      } else {
        await post(`/api/console/conversations/${routeId}/contact-blacklist`, { scope: 'thread' });
        setContactBlocked(true);
      }
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'No se pudo actualizar la lista negra');
    } finally {
      setBlacklistBusy(false);
    }
  }, [routeId, canManageBlacklist, contactBlocked, post]);

  const manualRefresh = useCallback(async () => {
    if (!routeId) return;
    setRefreshing(true);
    setErr(null);
    try {
      await reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Error');
    } finally {
      setRefreshing(false);
    }
  }, [routeId, reload]);

  if (!routeId) return <p className="pwa-muted">ID inválido</p>;

  const title = displayVisitorLabel(conv?.visitorName ?? null, {
    fallback: 'Conversación',
    externalThreadId: conv?.externalThreadId,
    channel: conv?.channel,
  });
  const subtitle = conv
    ? `${conv.virtualAgent?.name ?? '—'} · ${conv.controlMode}${contactBlocked ? ' · lista negra' : ''} · ${conv.status}`
    : '';
  const lastMessageAt =
    conv && conv.messages.length > 0 ? conv.messages[conv.messages.length - 1]?.timestamp : null;
  const waWindow = conv?.whatsappCustomerWindow;
  const remainingLive = remainingWindowMs(waWindow?.lastVisitorAt, windowClock);
  const lockComposer = Boolean(
    conv?.channel === 'whatsapp_meta' && remainingLive <= 0 && (waWindow?.templates.length ?? 0) > 0
  );
  const waTemplates = waWindow?.templates ?? [];

  return (
    <div className="chats-thread">
      <header className="chats-thread__header">
        <ScreenBackButton to="/conversations" />
        <div className="chats-thread__header-main">
          <div className="chats-thread__title-row">
            <h1 className="chats-thread__title">
              {conv ?
                <ConversationChannelIcon channel={conv.channel} size={18} className="chats-thread__title-icon" />
              : null}
              <span className="chats-thread__title-text">{title}</span>
            </h1>
            <div className="chats-thread__title-actions">
              <button
                type="button"
                className="chats-thread__icon-btn"
                title="Información de la conversación"
                aria-label="Información de la conversación"
                aria-expanded={infoOpen}
                onClick={() => setInfoOpen(true)}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                </svg>
              </button>
              {hasAgentCalendar && canReserve ? (
                <button
                  type="button"
                  className="chats-thread__icon-btn"
                  title="Reservar"
                  aria-label="Reservar"
                  onClick={() => {
                    setReservePrefill(null);
                    setReserveOpen(true);
                  }}
                >
                  <IconCalendar aria-hidden />
                </button>
              ) : null}
              {hasContactBlacklist && canManageBlacklist ? (
                <button
                  type="button"
                  className={[
                    'chats-thread__icon-btn',
                    contactBlocked ? 'chats-thread__icon-btn--active' : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  title={contactBlocked ? 'Quitar de lista negra' : 'Agregar a lista negra'}
                  aria-label={contactBlocked ? 'Quitar de lista negra' : 'Agregar a lista negra'}
                  aria-pressed={contactBlocked}
                  disabled={blacklistBusy}
                  onClick={() => void toggleContactBlacklist()}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                    <circle cx="12" cy="12" r="9" />
                    <line x1="5" y1="5" x2="19" y2="19" />
                  </svg>
                </button>
              ) : null}
              <button
                type="button"
                className="chats-thread__icon-btn"
                title="Actualizar mensajes"
                aria-label="Actualizar conversación"
                disabled={refreshing}
                onClick={() => void manualRefresh()}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M17.65 6.35A7.958 7.958 0 0012 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08A5.99 5.99 0 0112 18c-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
                </svg>
              </button>
              <button
                type="button"
                className={[
                  'chats-thread__icon-btn',
                  conv?.controlMode === 'human' ? 'chats-thread__icon-btn--active' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                title="Modo humano"
                aria-label="Modo humano"
                aria-pressed={conv?.controlMode === 'human'}
                onClick={() => void setMode('human')}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
              </button>
              <button
                type="button"
                className={[
                  'chats-thread__icon-btn',
                  conv?.controlMode === 'auto' ? 'chats-thread__icon-btn--active' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                title="Modo automático (IA)"
                aria-label="Modo automático"
                aria-pressed={conv?.controlMode === 'auto'}
                onClick={() => void setMode('auto')}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M9 2h1v2H9V2zm5 0h1v2h-1V2zM7 6h10a3 3 0 0 1 3 3v7a3 3 0 0 1-3 3H7a3 3 0 0 1-3-3V9a3 3 0 0 1 3-3zm3.5 6.25a1.35 1.35 0 1 0 0 2.7 1.35 1.35 0 0 0 0-2.7zm5 0a1.35 1.35 0 1 0 0 2.7 1.35 1.35 0 0 0 0-2.7zM10 16.25h4v1.25h-4v-1.25z" />
                </svg>
              </button>
            </div>
          </div>
          {subtitle ? <p className="chats-thread__subtitle">{subtitle}</p> : null}
          {conv?.whatsappCustomerWindow?.showCountdown ?
            <p className="chats-thread__wa-window" role="status">
              WhatsApp ·{' '}
              {formatWhatsappWindowRemaining(
                remainingWindowMs(conv.whatsappCustomerWindow.lastVisitorAt, windowClock)
              )}
            </p>
          : null}
        </div>
      </header>

      {err ? <p className="pwa-err chats-thread__banner">{err}</p> : null}
      {reserveNotice ? <p className="pwa-muted chats-thread__banner">{reserveNotice}</p> : null}

      {infoOpen ?
        <div className="pwa-info-sheet" role="dialog" aria-modal="true" aria-labelledby="conv-info-title">
          <button
            type="button"
            className="pwa-info-sheet__backdrop"
            aria-label="Cerrar información"
            onClick={() => setInfoOpen(false)}
          />
          <div className="pwa-info-sheet__panel">
            <header className="pwa-info-sheet__header">
              <h2 id="conv-info-title" className="pwa-info-sheet__title">
                Información
              </h2>
              <button
                type="button"
                className="pwa-info-sheet__close"
                aria-label="Cerrar"
                onClick={() => setInfoOpen(false)}
              >
                ×
              </button>
            </header>
            <dl className="pwa-info-sheet__dl">
              <div className="pwa-info-sheet__row">
                <dt>ID conversación</dt>
                <dd>{conv?.id ?? routeId}</dd>
              </div>
              <div className="pwa-info-sheet__row">
                <dt>Canal</dt>
                <dd>{conv ? channelLabel(conv.channel) : '—'}</dd>
              </div>
              {sanitizeVisitorName(conv?.visitorName) && !isAutoGeneratedVisitorName(conv?.visitorName) ?
                <div className="pwa-info-sheet__row">
                  <dt>Contacto</dt>
                  <dd>{sanitizeVisitorName(conv?.visitorName)}</dd>
                </div>
              : null}
              <div className="pwa-info-sheet__row">
                <dt>Inicio</dt>
                <dd>{conv?.createdAt ? formatConversationDateTime(conv.createdAt) : '—'}</dd>
              </div>
              <div className="pwa-info-sheet__row">
                <dt>Última actividad</dt>
                <dd>
                  {lastMessageAt ?
                    formatConversationDateTime(lastMessageAt)
                  : conv?.updatedAt ?
                    formatConversationDateTime(conv.updatedAt)
                  : '—'}
                </dd>
              </div>
              <div className="pwa-info-sheet__row">
                <dt>Mensajes</dt>
                <dd>{conv ? conv.messages.length.toLocaleString('es') : '—'}</dd>
              </div>
              <div className="pwa-info-sheet__row">
                <dt>Estado</dt>
                <dd>{conv ? formatConversationStatus(conv.status) : '—'}</dd>
              </div>
              <div className="pwa-info-sheet__row">
                <dt>Modo</dt>
                <dd>{conv ? formatControlMode(conv.controlMode) : '—'}</dd>
              </div>
              <div className="pwa-info-sheet__row">
                <dt>Agente</dt>
                <dd>{conv?.virtualAgent?.name ?? conv?.workflow?.name ?? '—'}</dd>
              </div>
              {conv?.hasLeadQualificationPlugin ? (
                <div className="pwa-info-sheet__row pwa-info-sheet__row--lead">
                  <dt>Lead</dt>
                  <dd>
                    <PwaLeadQualityBlock
                      canWrite={can('pwa.conversations.write')}
                      states={conv.leadMode === 'internal' ? conv.leadStates : undefined}
                      value={{
                        leadQuality: conv.leadQuality ?? null,
                        leadReason: conv.leadReason ?? null,
                        leadFitDetails: conv.leadFitDetails ?? [],
                        leadNote: conv.leadNote ?? null,
                        leadRatedSource: conv.leadRatedSource ?? null,
                      }}
                      onSave={async (body) => {
                        await patch(`/api/console/conversations/${routeId}`, body);
                        setConv((c) => (c ? { ...c, ...body, leadRatedSource: 'human' } : c));
                      }}
                    />
                  </dd>
                </div>
              ) : null}
              {conv?.externalThreadId ?
                <div className="pwa-info-sheet__row">
                  <dt>ID externo</dt>
                  <dd className="pwa-info-sheet__mono">{conv.externalThreadId}</dd>
                </div>
              : null}
              {conv?.channelIntegrationId ?
                <div className="pwa-info-sheet__row">
                  <dt>Integración</dt>
                  <dd className="pwa-info-sheet__mono">{conv.channelIntegrationId}</dd>
                </div>
              : null}
            </dl>
          </div>
        </div>
      : null}

      {improveForId ?
        <div className="pwa-info-sheet" role="dialog" aria-modal="true" aria-labelledby="improve-title">
          <button
            type="button"
            className="pwa-info-sheet__backdrop"
            aria-label="Cerrar"
            onClick={() => setImproveForId(null)}
          />
          <div className="pwa-info-sheet__panel">
            <header className="pwa-info-sheet__header">
              <h2 id="improve-title" className="pwa-info-sheet__title">
                Mejorar respuesta
              </h2>
              <button
                type="button"
                className="pwa-info-sheet__close"
                aria-label="Cerrar"
                onClick={() => setImproveForId(null)}
              >
                ×
              </button>
            </header>
            <form className="chat-improve-form" onSubmit={(ev) => void submitImprove(ev)}>
              <label className="chat-improve-form__label" htmlFor="improve-comment">
                Qué estuvo mal
              </label>
              <textarea
                id="improve-comment"
                className="pwa-field chat-improve-form__field"
                rows={3}
                value={improveComment}
                onChange={(ev) => setImproveComment(ev.target.value)}
                placeholder="Describí el problema…"
              />
              <label className="chat-improve-form__label" htmlFor="improve-desired">
                Cómo debería responder
              </label>
              <textarea
                id="improve-desired"
                className="pwa-field chat-improve-form__field"
                rows={4}
                value={improveDesired}
                onChange={(ev) => setImproveDesired(ev.target.value)}
                placeholder="Escribí la respuesta correcta…"
              />
              <div className="chat-improve-form__actions">
                <button
                  type="button"
                  className="pwa-btn"
                  disabled={improveBusy}
                  onClick={() => setImproveForId(null)}
                >
                  Cancelar
                </button>
                <button type="submit" className="pwa-btn pwa-btn--primary" disabled={improveBusy}>
                  {improveBusy ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      : null}

      {deleteIds ? (
        <div className="pwa-info-sheet" role="dialog" aria-modal="true" aria-labelledby="delete-msg-title">
          <button
            type="button"
            className="pwa-info-sheet__backdrop"
            aria-label="Cancelar"
            disabled={deleteBusy}
            onClick={() => setDeleteIds(null)}
          />
          <div className="pwa-info-sheet__panel">
            <header className="pwa-info-sheet__header">
              <h2 id="delete-msg-title" className="pwa-info-sheet__title">
                Eliminar para todos
              </h2>
            </header>
            <p className="chat-delete-sheet__hint">
              {conv?.channel === 'whatsapp_meta'
                ? 'Deja de verse en este chat. En WhatsApp el mensaje puede seguir en el teléfono del visitante.'
                : conv?.channel === 'whatsapp_js'
                  ? 'Se borra para todos, también en el WhatsApp del visitante.'
                  : 'Se borra para todos. El visitante tampoco lo va a ver.'}
            </p>
            <div className="chat-delete-sheet__actions">
              <button
                type="button"
                className="pwa-btn"
                disabled={deleteBusy}
                onClick={() => setDeleteIds(null)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="pwa-btn pwa-btn--danger"
                disabled={deleteBusy}
                onClick={() => {
                  const ids = deleteIds;
                  if (!routeId || !ids?.length) return;
                  setDeleteBusy(true);
                  void (async () => {
                    try {
                      let channelRevoked: boolean | null = null;
                      for (const messageId of ids) {
                        const data = await post<{ channelRevoked: boolean | null }>(
                          `/api/console/conversations/${routeId}/messages/${encodeURIComponent(messageId)}/delete-for-everyone`,
                          {}
                        );
                        channelRevoked = data.channelRevoked;
                      }
                      const gone = new Set(ids);
                      setConv((current) =>
                        current
                          ? { ...current, messages: current.messages.filter((m) => !gone.has(m.id)) }
                          : current
                      );
                      setDeleteIds(null);
                      if (conv?.channel === 'whatsapp_js' && channelRevoked === false) {
                        setLockedHint('Se ocultó en este chat, pero no se pudo borrar en WhatsApp.');
                      }
                    } catch (e) {
                      setErr(e instanceof ApiError ? e.message : 'No se pudo borrar el mensaje');
                      setDeleteIds(null);
                    } finally {
                      setDeleteBusy(false);
                    }
                  })();
                }}
              >
                {deleteBusy ? 'Borrando…' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {lockedHint ? (
        <p className="chats-thread__toast" role="status">
          {lockedHint}
        </p>
      ) : null}

      <div ref={scrollRef} className="chats-thread__messages">
        {groupConsecutiveImageMessages(conv?.messages ?? []).map((item) =>
          item.type === 'album' ?
            <div
              key={item.id}
              className={mensajeId && item.messageIds.includes(mensajeId) ? 'chat-bubble--linked' : undefined}
            >
              {item.messageIds.map((messageId) => (
                <span key={messageId} id={`mensaje-${messageId}`} />
              ))}
              <ChatAlbumRow
                sender={item.sender}
                images={item.images}
                timestamp={item.timestamp}
                deliveryStatus={item.deliveryStatus}
                channel={conv?.channel}
                onLongPress={
                  canManageBlacklist && item.sender !== 'visitor'
                    ? () => setDeleteIds(item.messageIds)
                    : undefined
                }
                onRetryDelivery={
                  canManageBlacklist &&
                  conv &&
                  isWhatsappChannel(conv.channel) &&
                  item.messageIds.some((messageId) => {
                    const message = conv.messages.find((row) => row.id === messageId);
                    return isRetryableOutboundStatus(message?.deliveryStatus);
                  })
                    ? () => {
                        for (const messageId of item.messageIds) {
                          const message = conv.messages.find((row) => row.id === messageId);
                          if (isRetryableOutboundStatus(message?.deliveryStatus)) {
                            void retryDelivery(messageId);
                          }
                        }
                      }
                    : undefined
                }
                retryingDelivery={item.messageIds.some((messageId) => retryingIds.has(messageId))}
                retryReady={item.messageIds.some((messageId) => {
                  const message = conv?.messages.find((row) => row.id === messageId);
                  if (!message || !isRetryableOutboundStatus(message.deliveryStatus)) return false;
                  return outboundRetryReady(
                    message.deliveryStatus,
                    message.timestamp,
                    deliveryClock,
                    lastRetryAtRef.current.get(messageId)
                  );
                })}
              />
            </div>
          : <ChatMessageBubble
              key={item.message.id}
              message={item.message}
              highlighted={mensajeId === item.message.id}
              channel={conv?.channel}
              onLongPress={
                canManageBlacklist && item.message.sender !== 'visitor'
                  ? () => setDeleteIds([item.message.id])
                  : undefined
              }
              thumbsDownLocked={isFixedHandoffAgentMessage(item.message)}
              onThumbsDown={
                item.message.sender === 'agent' && !item.message.isWhisper
                  ? () => openImprove(item.message)
                  : undefined
              }
              onRetryDelivery={
                canManageBlacklist &&
                conv &&
                isWhatsappChannel(conv.channel) &&
                isRetryableOutboundStatus(item.message.deliveryStatus)
                  ? () => void retryDelivery(item.message.id)
                  : undefined
              }
              retryingDelivery={retryingIds.has(item.message.id)}
              retryReady={outboundRetryReady(
                item.message.deliveryStatus,
                item.message.timestamp,
                deliveryClock,
                lastRetryAtRef.current.get(item.message.id)
              )}
            />
        )}
        {conv?.visitorTyping ? <ChatTypingBubble /> : null}
      </div>

      <footer className="chats-thread__composer">
        {lockComposer ?
          <div className="chats-thread__template-bar">
            <p className="chats-thread__template-hint">Ventana de 24 h cerrada. Enviá una plantilla.</p>
            <div className="chats-thread__template-row">
              <label className="chats-thread__template-label" htmlFor="wa-template-select">
                Plantilla
              </label>
              <select
                id="wa-template-select"
                className="chats-thread__template-select"
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                disabled={sendingTemplate}
              >
                {waTemplates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name} ({t.languageCode})
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="chats-thread__send chats-thread__template-send"
                disabled={sendingTemplate || !templateId}
                onClick={() => void sendTemplate()}
              >
                {sendingTemplate ? 'Enviando…' : 'Enviar'}
              </button>
            </div>
          </div>
        : <>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={CHAT_ACCEPT}
          className="chats-thread__file-input"
          aria-hidden
          tabIndex={-1}
          onChange={(e) => {
            onPickFiles(e.target.files);
            e.target.value = '';
          }}
        />
        {pendingFiles.length > 0 ?
          <div className="chats-thread__pending-files">
            {pendingFiles.map((f, i) => (
              <span key={`${f.name}-${i}-${f.size}`} className="chats-thread__pending-chip">
                <span className="chats-thread__pending-name">{f.name}</span>
                <button
                  type="button"
                  className="chats-thread__pending-remove"
                  aria-label={`Quitar ${f.name}`}
                  onClick={() => removePendingFile(i)}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        : null}
        <div className="chats-thread__composer-row">
        <button
          type="button"
          className="chats-thread__attach"
          title="Adjuntar imágenes o PDF (máx. 4)"
          aria-label="Adjuntar archivos"
          disabled={sending}
          onClick={openAttach}
        >
          <svg viewBox="0 0 24 24" fill="currentColor" width="22" height="22" aria-hidden>
            <path d="M16.5 6v11.5a4 4 0 1 1-8 0V5a2.5 2.5 0 0 1 5 0v10.5a1.5 1.5 0 0 1-3 0V6H10v9.5a2.5 2.5 0 0 0 5 0V5a4 4 0 0 0-8 0v12.5a6 6 0 0 0 12 0V6h-2.5z" />
          </svg>
        </button>
        <textarea
          value={text}
          onChange={(e) => {
            const v = e.target.value;
            setText(v);
            if (typingIdleRef.current) {
              clearTimeout(typingIdleRef.current);
              typingIdleRef.current = null;
            }
            if (!routeId) return;
            if (!v.trim()) {
              signalOperatorTyping(false);
              return;
            }
            signalOperatorTyping(true);
            typingIdleRef.current = setTimeout(() => signalOperatorTyping(false), 2200);
          }}
          onBlur={() => {
            if (typingIdleRef.current) {
              clearTimeout(typingIdleRef.current);
              typingIdleRef.current = null;
            }
            signalOperatorTyping(false);
          }}
          placeholder="Escribí un mensaje…"
          rows={1}
          className="chats-thread__input"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void sendHuman();
            }
          }}
        />
        {!text.trim() && pendingFiles.length === 0 ?
          <VoiceNoteRecorder
            disabled={sending}
            onRecordingComplete={(blob, mime) => void sendVoiceNote(blob, mime)}
            onTooShort={() => setErr('Mantené pulsado un poco más para grabar el audio')}
            unsupportedFallback={
              <button
                type="button"
                className="chats-thread__send"
                disabled={sending || (!text.trim() && pendingFiles.length === 0)}
                onClick={() => void sendHuman()}
                aria-label="Enviar"
              >
                <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              </button>
            }
          />
        : <button
            type="button"
            className="chats-thread__send"
            disabled={sending || (!text.trim() && pendingFiles.length === 0)}
            onClick={() => void sendHuman()}
            aria-label="Enviar"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
              <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
            </svg>
          </button>}
        </div>
        </>}
      </footer>
      {attachOpen ?
        <ChatAttachSheet
          groups={photoGroups ?? EMPTY_RESOURCE_PHOTO_GROUPS}
          loading={photoGroups === null}
          selectedKeys={selectedMediaKeys}
          busyKey={busyMediaKey}
          error={attachErr}
          loadPreview={loadAttachPreview}
          onToggle={(group, image) => void toggleResourcePhoto(group, image)}
          onSelectAll={(group) => void selectAllResourcePhotos(group)}
          pendingCount={pendingFiles.length}
          sending={sending}
          onSend={() => {
            void (async () => {
              const sent = await sendHuman();
              if (sent) setAttachOpen(false);
            })();
          }}
          onDeviceFiles={() => {
            setAttachOpen(false);
            fileInputRef.current?.click();
          }}
          onClose={() => setAttachOpen(false)}
        />
      : null}
      {reserveOpen && routeId ?
        <ChatReservationSheet
          conversationId={routeId}
          prefill={reservePrefill}
          onClose={() => setReserveOpen(false)}
          onCreated={(message) => setReserveNotice(message)}
        />
      : null}
    </div>
  );
}
